import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import logo from "../../assets/images/logo.svg";
import http from "../../services/http";
import "./ActivationPage.scss";
import ActivationForm from "./screens/ActivationForm";
import LoadingScreen from "./screens/LoadingScreen";
import LockedScreen from "./screens/LockedScreen";
import NotFoundScreen from "./screens/NotFoundScreen";
import ReviewScreen from "./screens/ReviewScreen";
import SuccessScreen from "./screens/SuccessScreen";

// ─── screens ────────────────────────────────────────────────────────────────
// loading   → spinner while we check the token status
// not-found → invalid / unknown token
// form      → blank form (first-time submission)
// thank-you → shown after a successful first submission
// review    → details already submitted; shows masked values + update link
// editing   → correction form (with warning banner)
// updated   → shown after a successful correction
// locked    → details already sent to provider; no editing allowed
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_FORM = { imei: "", eid: "", activationDate: "" };

const ActivationPage = () => {
  const { token } = useParams();

  const [screen, setScreen]               = useState("loading");
  const [submittedInfo, setSubmittedInfo] = useState(null);
  const [itemMeta, setItemMeta]           = useState({ itemNumber: 1, totalItems: 1, productName: "" });
  const [form, setForm]                   = useState(EMPTY_FORM);
  const [errors, setErrors]               = useState({});
  const [submitting, setSubmitting]       = useState(false);

  const minSelectableDate = new Date();
  minSelectableDate.setDate(minSelectableDate.getDate() + 3);

  // ── Check token status on mount ──────────────────────────────────────────
  useEffect(() => {
    if (!token) { setScreen("not-found"); return; }

    const checkStatus = async () => {
      try {
        const data = await http.get(`/order/activate/${token}`);

        const { itemNumber, totalItems, productName } = data;
        setItemMeta({ itemNumber: itemNumber ?? 1, totalItems: totalItems ?? 1, productName: productName ?? "" });

        if (data.status === "pending") {
          setScreen("form");
        } else if (data.status === "submitted") {
          setSubmittedInfo(data);
          setScreen(data.sentToProvider ? "locked" : "review");
        }
      } catch {
        setScreen("not-found");
      }
    };

    checkStatus();
  }, [token]);

  // ── Validation ───────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    const imei = form.imei.trim();
    const eid  = form.eid.trim();

    if (!imei)                       errs.imei = "IMEI is required.";
    else if (!/^\d+$/.test(imei))    errs.imei = "IMEI must contain only numeric digits.";
    else if (imei.length !== 15)     errs.imei = "IMEI must be exactly 15 digits long.";

    if (!eid)                        errs.eid = "EID is required.";
    else if (!/^\d+$/.test(eid))     errs.eid = "EID must contain only numeric digits.";
    else if (eid.length !== 32)      errs.eid = "EID must be exactly 32 digits long.";

    if (!form.activationDate)        errs.activationDate = "Activation date is required.";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => ({ ...e, [name]: "" }));
  };

  const handleDateChange = (date) => {
    setForm((f) => ({ ...f, activationDate: date }));
    setErrors((e) => ({ ...e, activationDate: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      await http.post(`/order/activate/${token}`, form);
      setScreen(screen === "editing" ? "updated" : "thank-you");
    } catch {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartEditing = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setScreen("editing");
  };

  const handleCancelEditing = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setScreen("review");
  };

  // ── Screen map ───────────────────────────────────────────────────────────
  const renderScreen = () => {
    switch (screen) {
      case "loading":   return <LoadingScreen />;
      case "not-found": return <NotFoundScreen />;
      case "thank-you": return <SuccessScreen isUpdate={false} />;
      case "updated":   return <SuccessScreen isUpdate={true} />;
      case "review":    return <ReviewScreen submittedInfo={submittedInfo} onEdit={handleStartEditing} />;
      case "locked":    return <LockedScreen submittedInfo={submittedInfo} />;
      case "form":
      case "editing":
        return (
          <ActivationForm
            isEditing={screen === "editing"}
            form={form}
            errors={errors}
            submitting={submitting}
            minSelectableDate={minSelectableDate}
            onChange={handleChange}
            onDateChange={handleDateChange}
            onSubmit={handleSubmit}
            onCancel={handleCancelEditing}
            itemNumber={itemMeta.itemNumber}
            totalItems={itemMeta.totalItems}
            productName={itemMeta.productName}
          />
        );
      default: return <NotFoundScreen />;
    }
  };

  // ── Shell (logo always visible) ──────────────────────────────────────────
  return (
    <div className="activation-page">
      <div className="activation-page__inner">
        <img src={logo} alt="Blikst" className="activation-page__logo" />
        {renderScreen()}
      </div>
    </div>
  );
};

export default ActivationPage;
